import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import Shell from "./Shell";
import { useAdminStyles, num, dateTime } from "./styles";

// rótulos + cor do status de teste do número
const TEST_LABEL = { passed: "OK", partial: "Parcial", failed: "Falhou", testing: "Testando", untested: "Não testado" };
const testPill = (classes, s) => {
  const k = s === "passed" ? "ok" : s === "failed" ? "bad" : s === "partial" || s === "testing" ? "warn" : "neutral";
  return <span className={`${classes.pill} ${classes[k]}`}>{TEST_LABEL[s] || s || "—"}</span>;
};

// Telefonia (backoffice): saúde da base de números + chamadas + erros, e a ação
// de reprovisionar um número que perdeu o roteamento no jambonz.
const Telephony = () => {
  const classes = useAdminStyles();
  const { confirm } = useConfirm();
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null); // id em reprovisionamento

  const load = useCallback(() => {
    api.get("/admin/telephony").then(r => setD(r.data)).catch(toastError);
  }, []);
  useEffect(() => load(), [load]);

  const reprovision = async (row) => {
    const ok = await confirm({
      title: "Reprovisionar número",
      message: `Reprovisionar ${row.number}${row.ownerName ? ` (${row.ownerName})` : ""} no jambonz e reapontar o roteamento de entrada?`,
      confirmText: "Reprovisionar",
    });
    if (!ok) return;
    setBusy(row.id);
    try {
      const { data } = await api.post(`/admin/telephony/numbers/${row.id}/reprovision`);
      const rota = data.routed ? ` · roteado p/ ${data.routedTo || "app de entrada"}` : "";
      toast.success(`Reprovisionado: ${data.status}${rota}.`);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  if (!d) return <Shell title="Telefonia"><div className={classes.empty}>Carregando…</div></Shell>;

  const { numbers, clients, calls, errors, list } = d;
  const temErro = errors.count7 > 0;
  const filtered = filter === "all" ? list : list.filter(r => (r.testStatus || "untested") === filter);
  const FILTERS = [
    { key: "all", label: `Todos (${num(numbers.total)})` },
    { key: "failed", label: `Falhou (${num(numbers.tested.failed)})` },
    { key: "partial", label: `Parcial (${num(numbers.tested.partial)})` },
    { key: "passed", label: `OK (${num(numbers.tested.passed)})` },
    { key: "untested", label: `Não testado (${num(numbers.tested.untested)})` },
  ];

  return (
    <Shell title="Telefonia" right={<div className={classes.tab} onClick={load}>Atualizar</div>}>
      <div className={classes.cards}>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Números no sistema</div>
          <div className={classes.kpiValue}>{num(numbers.total)}</div>
          <div className={classes.kpiHint}>{num(numbers.assigned)} com dono · {num(numbers.pool)} no pool</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Clientes com número</div>
          <div className={classes.kpiValue}>{num(clients.withNumbers)}</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Chamadas (30d)</div>
          <div className={classes.kpiValue}>{num(calls.d30)}</div>
          <div className={classes.kpiHint}>{num(calls.today)} hoje · {num(calls.inbound30)} entra / {num(calls.outbound30)} sai</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Erros de chamada (7d)</div>
          <div className={classes.kpiValue}>
            {num(errors.count7)}{" "}
            {temErro && <span className={`${classes.pill} ${classes.bad}`} style={{ verticalAlign: "middle" }}>atenção</span>}
          </div>
          <div className={classes.kpiHint}>{num(errors.count24)} nas últimas 24h</div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Erros de chamada recentes</div>
        <div className={classes.cardSub}>
          Eventos de chamada com status de erro (motivo do jambonz). {errors.list.length ? "" : "Nenhum erro recente."}
        </div>
        {!!errors.list.length && (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  {["Quando", "Tipo", "Motivo", "Fluxo", "Cliente", "Call SID"].map(h => (
                    <th key={h} className={`${classes.th} ${classes.thPlain}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {errors.list.map(e => (
                  <tr key={e.id}>
                    <td className={classes.td}>{dateTime(e.createdAt)}</td>
                    <td className={classes.td}><span className={classes.muted}>{e.type}</span></td>
                    <td className={classes.td}>
                      <span className={`${classes.pill} ${classes.bad}`}>{e.terminationReason || e.message || "erro"}</span>
                    </td>
                    <td className={classes.td}>{e.appName || "—"}</td>
                    <td className={classes.td}>{e.ownerName || "—"}</td>
                    <td className={classes.td}><span className={classes.muted}>{e.callSid || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Números</div>
        <div className={classes.cardSub}>Falhos primeiro. Use “Reprovisionar” num número que perdeu o roteamento no jambonz.</div>
        <div className={classes.filters}>
          {FILTERS.map(f => (
            <div
              key={f.key}
              className={`${classes.chip} ${filter === f.key ? classes.chipOn : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </div>
          ))}
        </div>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                {["Número", "Cliente", "Cidade", "Entrada", "Teste", ""].map((h, i) => (
                  <th key={i} className={`${classes.th} ${classes.thPlain}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td className={classes.td}><div className={classes.strong}>{r.number}</div>{r.label ? <div className={classes.muted}>{r.label}</div> : null}</td>
                  <td className={classes.td}>{r.ownerName || <span className={classes.muted}>— (pool)</span>}{r.ownerEmail ? <div className={classes.muted}>{r.ownerEmail}</div> : null}</td>
                  <td className={classes.td}>{r.city || "—"}</td>
                  <td className={classes.td}>{r.inboundApp || <span className={classes.muted}>sem app</span>}</td>
                  <td className={classes.td}>
                    {testPill(classes, r.testStatus)}
                    {r.lastTestReason ? <div className={classes.muted} style={{ marginTop: 3, whiteSpace: "normal", maxWidth: 320 }}>{r.lastTestReason}</div> : null}
                  </td>
                  <td className={classes.td} style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className={classes.chip}
                      disabled={busy === r.id || !r.ownerId}
                      onClick={() => reprovision(r)}
                      title={r.ownerId ? "Reprovisionar no jambonz" : "Número sem dono (pool)"}
                    >
                      {busy === r.id ? "Reprovisionando…" : "Reprovisionar"}
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td className={classes.td} colSpan={6}><span className={classes.empty}>Nenhum número neste filtro.</span></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
};

export default Telephony;
